import { ActiveElement, BarController, BarElement, CategoryScale, Chart, ChartEvent, TimeScale, Tooltip } from 'chart.js';
import moment from 'moment';
import React from 'react';
import './chartjsAdapterMoment';
import { IWithReactRouterProps, withReactRouter } from '../../mixins/router/withReactRouter';
import { Component } from './Component';

Chart.register(BarController);
Chart.register(CategoryScale);
Chart.register(TimeScale);
Chart.register(BarElement);
Chart.register(Tooltip);

interface ITimelineChartProps extends IWithReactRouterProps {
    data: { title: string; startDate?: string; endDate?: string; tooltipBodyText: string; backgroundColor?: string; url?: string }[];
}

class TimelineChartComponent extends Component<ITimelineChartProps> {
    private canvasRef: any;
    private myChart: any;

    constructor(props: ITimelineChartProps) {
        super(props);
        this.canvasRef = React.createRef();
    }

    public override componentDidUpdate() {
        const formattedData = this.getFormattedData();
        this.myChart.data.labels = formattedData.labels;
        this.myChart.data.datasets[0] = formattedData.datasets[0];
        this.myChart.update();
    }

    public override componentDidMount() {
        const onClick = (_mouseEvent: ChartEvent, elements: ActiveElement[], _chart: Chart) => {
            if (elements[0]) {
                const activeElement = elements[0];
                const url = this.props.data[activeElement.index]?.url;
                if (url) {
                    this.props.navigate(url);
                }
            }
        };

        const dateRange = this.getDateRange();
        const formattedData = this.getFormattedData();
        this.myChart = new Chart(this.canvasRef.current, {
            type: 'bar',
            options: {
                indexAxis: 'y',
                plugins: {
                    legend: {
                        display: false,
                    },
                    tooltip: {
                        displayColors: false,
                        callbacks: {
                            label(tooltipItem) {
                                return (tooltipItem.raw as string[])[3]!.split('\n');
                            },
                        },
                    },
                },
                maintainAspectRatio: false,

                scales: {
                    x: {
                        type: 'time',
                        time: {
                            unit: 'year',
                            round: 'day',
                            displayFormats: {
                                year: 'YYYY',
                            },
                        },
                        min: moment(dateRange.startYear, 'YYYY').toISOString(),
                        max: moment(dateRange.endYear, 'YYYY').toISOString(),
                    },
                },

                onClick,
            },
            data: formattedData,
        });
    }

    public override render() {
        let height = (this.props.data.length + 1) * 50;
        if (height < 100) {
            height = 100;
        }
        return (
            <div className="pb40">
                <div style={{ height }}>
                    <canvas ref={this.canvasRef} />
                </div>
            </div>
        );
    }

    private getFormattedData() {
        const defaultBackgroundColor = 'rgba(44, 44, 44, 0.7)';

        return {
            labels: this.props.data.map((i) => i.title) as any,
            datasets: [
                {
                    backgroundColor: this.props.data.map((i) => i.backgroundColor ?? defaultBackgroundColor) as any,
                    data: this.props.data.map((i) => [
                        i.startDate ? moment(i.startDate) : undefined,
                        i.endDate ? moment(i.endDate) : i.startDate ? moment(i.startDate).add(1, 'days') : undefined,
                        i.title,
                        i.tooltipBodyText,
                    ]) as any,
                },
            ],
        };
    }

    private getDateRange() {
        const endDates = this.props.data.filter((i) => i.endDate).map((i) => moment(i.endDate));
        const startDates = this.props.data.filter((i) => i.startDate).map((i) => moment(i.startDate));

        const maxDate = moment.max([moment(), ...endDates, ...startDates]);
        const minDate = moment.min([moment(), ...endDates, ...startDates]);

        return { startYear: parseInt(minDate.format('YYYY'), 10), endYear: parseInt(maxDate.format('YYYY'), 10) + 1 };
    }
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const TimelineChart = withReactRouter(TimelineChartComponent);
